import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Button, Icon, TextField } from '@bloo-team/ui'
import { ArrowLeft } from '@bloo-team/ui/icons'
import {
  associationKind, authTypeLabel, isOrgEnabled, isUserActive,
  orgUpdatedDate, orgUserTotal,
} from '@bloo-team/api-client'
import { formatLastLogin } from '../../../lib/platform-users'
import { ConfirmStrip } from '../../../components/settings/ConfirmStrip'
import { useSettingsStrip } from '@bloo-team/core-shell'
import { Pagination } from '@bloo-team/ui'
import { OrgForm } from '../../../components/settings/OrgForm'
import {
  useAuthTypes,
  useOrgTenants,
  useOrgUsers,
  useOrganizationDetail,
  useSetOrgStatus,
  useUpdateOrganization,
} from '../../../lib/organizations'

/**
 * One organization (docs/settings-requirements.md §6.4).
 *
 * Three reads that the list cannot carry, and one of them is the reason this
 * page exists at all:
 *
 *   detail    `/org-details` — the auth policy, session timeout, when it was
 *             last changed, and how many of its people signed in this week.
 *   people    `user-list` WITH an `orgId`. The same endpoint Settings → Users
 *             calls flat, scoped server-side by ID — so this tab cannot be
 *             confused by two organizations sharing a name, which is the
 *             ambiguity that governs the Users delete (G13).
 *   tenants   `/association` — what this organization reaches. This is the
 *             read that makes disabling an informed decision rather than a
 *             hopeful one, which is why the disable confirm is HERE and names
 *             the tenants it would cut off.
 *
 * The people list is deliberately READ-ONLY. Every per-user write —
 * enable/disable, password reset, MFA reset, edit, remove — lives on
 * Settings → Users, which is gated to tenant admins and Root. This page is
 * reachable by an OrgAdmin, and duplicating those controls here would hand
 * credential operations to a gate that was never meant to carry them.
 */

/**
 * How many associations the disable confirm reads before it gives up naming
 * them. Large enough that truncation is the rare case, finite because the
 * endpoint is paged and "all of them" is not a request this API takes.
 */
const CONFIRM_PAGE = 200

function OrganizationPage() {
  const { clusterId, scopeId, orgId } = Route.useParams()

  const [userSearch, setUserSearch] = useState('')
  const [userQuery, setUserQuery] = useState('')
  const [userPage, setUserPage] = useState(1)
  const [userSize, setUserSize] = useState(25)
  const [tenantPage, setTenantPage] = useState(1)
  const [tenantSize, setTenantSize] = useState(25)
  const [editing, setEditing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [done, setDone] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setUserQuery(userSearch); setUserPage(1) }, 250)
    return () => clearTimeout(t)
  }, [userSearch])

  const detail = useOrganizationDetail(orgId)
  const users = useOrgUsers({ orgId, pageNo: userPage, pageSize: userSize, query: userQuery })
  const tenants = useOrgTenants({ orgId, page: tenantPage, size: tenantSize, query: '' })
  /**
   * A SECOND association read, for the confirm alone.
   *
   * The table's read is paged and the operator moves it. Naming
   * `tenants.data.tenants` in the confirm would name whichever page happens to
   * be on screen — page two's tenants, silently omitting page one's — which is
   * the "confirmed against a list nobody has seen" failure this confirm exists
   * to prevent. So the confirm asks for its own page-one read at a large size,
   * the way `useOrgResolution` does for the Users delete, and reports
   * truncation rather than presenting a cut-off list as the whole of it.
   *
   * Asked for unconditionally so it is already loaded when the confirm opens,
   * rather than fetched while the operator is reading it.
   */
  const assoc = useOrgTenants({ orgId, page: 1, size: CONFIRM_PAGE, query: '' })
  const assocRows = assoc.data?.tenants ?? []
  const assocTotal = assoc.data?.total ?? 0
  const assocTruncated = assocTotal > assocRows.length
  const authTypes = useAuthTypes()
  const update = useUpdateOrganization()
  const setStatus = useSetOrgStatus()

  const org = detail.data ?? null
  const enabled = org ? isOrgEnabled(org) : false
  const updated = org ? orgUpdatedDate(org) : null
  const confirmation = setStatus.error ? null : done

  /**
   * Whether the status write may be issued.
   *
   * ONE expression, read by both the button's disabled state and the handler.
   * They were two, and two that happen to agree today are two that can stop
   * agreeing — a disabled button is not a guard, it is a hint.
   *
   * Disabling waits for the associations, because the confirm names what it
   * would cut off and confirming against an unread list is the failure the
   * whole ordering exists to prevent. Enabling has nothing to wait for.
   */
  const canChangeStatus = org !== null && !setStatus.isPending && !(enabled && assoc.isLoading)

  const runStatus = () => {
    if (!org || !canChangeStatus) return
    const wasEnabled = enabled
    setStatus.mutate(org, {
      onSuccess: () => {
        setDone(`${org.orgName} is now ${wasEnabled ? 'disabled' : 'enabled'}.`)
        setConfirming(false)
      },
    })
  }

  const tenantRows = tenants.data?.tenants ?? []
  const userRows = users.data?.users ?? []

  useSettingsStrip({
    title: org?.orgName ?? (detail.isLoading ? 'Loading…' : 'Organization'),
    end: (
      <Link
        to="/$clusterId/$scopeId/settings/organizations"
        params={{ clusterId, scopeId }}
        className="bui-btn bui-btn-outline bui-btn-sm"
      >
        <Icon icon={ArrowLeft} size={12} /> All organizations
      </Link>
    ),
  })

  return (
    <div className="app-stack app-stack-wide">
      {detail.error ? <p className="sy-error" role="alert">{detail.error.message}</p> : null}

      {org ? (
        <section className="hud-panel sy-card" aria-label="Organization">
          <div className="sy-pane-head">
            <span className="hud-eyebrow">Overview</span>
            <span className="sy-pill" data-tone={enabled ? 'good' : 'warn'}>
              {enabled ? 'enabled' : 'disabled'}
            </span>
            <Button
              variant="outline" size="sm"
              isDisabled={setStatus.isPending}
              onPress={() => { update.reset(); setEditing(true) }}
            >
              Edit
            </Button>
            <Button
              variant="outline" size="sm"
              tone={enabled ? 'warn' : undefined}
              isDisabled={setStatus.isPending}
              onPress={() => { setStatus.reset(); setDone(null); setConfirming(true) }}
            >
              {enabled ? 'Disable' : 'Enable'}
            </Button>
          </div>

          {/* Flat dt/dd pairs: `.kv` is a two-column grid, so a wrapper
              element would become one grid cell and collapse the layout. */}
          <dl className="kv">
            <dt>Sign-in method</dt>
            <dd>{org.authType ? authTypeLabel(org.authType) : <span className="muted">—</span>}</dd>

            <dt>Session timeout</dt>
            <dd>
              {org.sessionTimeout != null && org.sessionTimeout !== ''
                ? `${org.sessionTimeout} min`
                : <span className="muted">—</span>}
            </dd>

            <dt>Administrators</dt>
            <dd className="mono">{org['Organization Admin']}</dd>

            <dt>People</dt>
            <dd className="mono">{orgUserTotal(org)}</dd>

            <dt>Signed in this week</dt>
            <dd className="mono">
              {org.orgUserActivityCount != null
                ? org.orgUserActivityCount
                : <span className="muted">—</span>}
            </dd>

            <dt>Last changed</dt>
            {/* 0 means never changed. Rendered as a date it reads 1970. */}
            <dd>{updated ? updated.toLocaleString('en-US', { dateStyle: 'medium' }) : <span className="muted">never</span>}</dd>
          </dl>

          {confirmation ? <p className="sy-test sy-test-ok" role="status">{confirmation}</p> : null}
          {setStatus.error ? <p className="sy-error" role="alert">{setStatus.error.message}</p> : null}

          {confirming ? (
            <ConfirmStrip
              label={`${enabled ? 'Disable' : 'Enable'} ${org.orgName}?`}
              message={
                enabled ? (
                  assoc.isLoading
                    ? `Checking which tenants ${org.orgName} reaches…`
                    : assocTruncated
                      // Naming the first 200 of 400 would read as exhaustive.
                      ? `Disable ${org.orgName}? Nobody in it will be able to sign in, and it is associated with ${assocTotal} tenants — too many to name here. Check the Tenants list below before you decide.`
                      : assocRows.length
                        ? `Disable ${org.orgName}? Nobody in it will be able to sign in, and it is associated with ${assocRows.map((t) => t.tenantName).join(', ')}.`
                        : `Disable ${org.orgName}? Nobody in it will be able to sign in. It is associated with no tenants.`
                ) : (
                  `Enable ${org.orgName}? People in it can sign in again, subject to their own status.`
                )
              }
              confirmLabel={enabled ? 'Disable' : 'Enable'}
              tone={enabled ? 'warn' : undefined}
              busy={setStatus.isPending}
              confirmDisabled={!canChangeStatus}
              onCancel={() => setConfirming(false)}
              onConfirm={runStatus}
            />
          ) : null}

          <p className="sy-help">
            Sign-in method applies to everyone here. Single sign-on can be selected as a policy,
            but the identity provider cannot be configured from this console yet.
          </p>
        </section>
      ) : null}

      <section className="hud-panel sy-card" aria-label="People">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">People</span>
          <span className="mono muted">{users.data?.total ?? 0}</span>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search people in this organization"
            value={userSearch}
            onChange={setUserSearch}
            placeholder="Search name or email…"
          />
        </div>

        {users.error ? <p className="sy-error" role="alert">{users.error.message}</p> : null}

        {!userRows.length && !users.isLoading && !users.error ? (
          <p className="sy-help">
            {userQuery ? `Nobody here matches “${userQuery}”.` : 'This organization has no people yet.'}
          </p>
        ) : null}

        {userRows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th className="num">Tenants</th>
                  <th>Last sign-in</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {userRows.map((u) => (
                  <tr key={u.userId}>
                    <td>{u.name || <span className="muted">no name</span>}</td>
                    <td className="mono">{u.emailId}</td>
                    <td>{u.userType || <span className="muted">—</span>}</td>
                    <td className="num">{u.TenantCount}</td>
                    <td className="muted">{formatLastLogin(u)}</td>
                    <td>
                      <span className="sy-pill" data-tone={isUserActive(u) ? 'good' : 'warn'}>
                        {isUserActive(u) ? 'active' : 'inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {(users.data?.total ?? 0) > 0 ? (
          <Pagination
            label="People pagination"
            page={userPage}
            pageSize={userSize}
            total={users.data?.total ?? 0}
            busy={users.isFetching}
            unit="people"
            onPageChange={setUserPage}
            onPageSizeChange={(n) => { setUserSize(n); setUserPage(1) }}
          />
        ) : null}

        <p className="sy-help">
          Read-only here. Editing someone, resetting their password or MFA, and removing them all
          live on Settings · Users, which is restricted to tenant administrators.
        </p>
      </section>

      <section className="hud-panel sy-card" aria-label="Tenants">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Tenants</span>
          <span className="mono muted">{tenants.data?.total ?? 0}</span>
        </div>

        {tenants.error ? <p className="sy-error" role="alert">{tenants.error.message}</p> : null}

        {!tenantRows.length && !tenants.isLoading && !tenants.error ? (
          <p className="sy-help">This organization is not associated with any tenant.</p>
        ) : null}

        {tenantRows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Association</th>
                </tr>
              </thead>
              <tbody>
                {tenantRows.map((t) => {
                  const kind = associationKind(t)
                  return (
                    <tr key={`${t.tenantName}:${t.tenantAssociation}`}>
                      <td>{t.tenantName || <span className="muted">—</span>}</td>
                      <td>
                        {kind === 'other'
                          // Unrecognised prose is shown as it arrived rather
                          // than bucketed into a label it may not mean.
                          ? (t.tenantAssociation || <span className="muted">—</span>)
                          : <span className="sy-pill">{kind}</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {(tenants.data?.total ?? 0) > 0 ? (
          <Pagination
            label="Tenant pagination"
            page={tenantPage}
            pageSize={tenantSize}
            total={tenants.data?.total ?? 0}
            busy={tenants.isFetching}
            unit="tenants"
            onPageChange={setTenantPage}
            onPageSizeChange={(n) => { setTenantSize(n); setTenantPage(1) }}
          />
        ) : null}

        <p className="sy-help">
          This list is read-only. Both association endpoints are addressed by tenant, so the
          connection is changed on Settings · Tenants — open the tenant you want to add this
          organization to, or remove it from.
        </p>
      </section>

      <OrgForm
        open={editing}
        mode="edit"
        detail={org}
        authTypes={authTypes.data ?? []}
        loading={detail.isLoading}
        busy={update.isPending}
        error={update.error?.message ?? null}
        onClose={() => setEditing(false)}
        onSubmit={(input) =>
          update.mutate({ orgId, ...input }, {
            onSuccess: () => { setEditing(false); setDone(`${input.orgName} was updated.`) },
          })
        }
      />
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/organizations_/$orgId')({
  component: OrganizationPage,
})
